import { type FC } from 'react';
import styled from 'styled-components';

import { FundDocumentType } from '@/api/funds/models';
import { getBaseApiUrl } from '@/config/utils';
import { useTexts } from '@/hooks/useTexts';
import { Locale } from '@/i18n/locale';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { Divider } from '../Divider';
import { DownloadLink } from './DownloadLink';
import { useFindDocumentByType } from './hooks/useFindDocumentByType';

interface Props {
  isin: string;
  headingsAriaLevel?: number;
}

export const Documents: FC<Props> = ({ isin, headingsAriaLevel = 2 }) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const kiid = useFindDocumentByType(isin, FundDocumentType.Kiid);
  const prospectus = useFindDocumentByType(isin, FundDocumentType.Prospectus);
  const monthlyReport = useFindDocumentByType(
    isin,
    FundDocumentType.MonthlyReport,
  );

  const fundDataUrl = `${getBaseApiUrl()}/open/savings/funds`;

  const getLanguageAsText = (languageCode: Locale): string => {
    switch (languageCode) {
      case Locale.Norwegian: {
        return texts.language.noLabel.toLowerCase();
      }
      case Locale.Swedish: {
        return texts.language.svLabel.toLowerCase();
      }
      case Locale.English: {
        return texts.language.enLabel.toLowerCase();
      }
      case Locale.Danish: {
        return texts.language.daLabel.toLowerCase();
      }
      default:
        throw new Error(`Invalid locale enum [${languageCode}]`);
    }
  };

  return (
    <DocumentsContainer data-test="fund-documents">
      <div
        className="font-body-demibold"
        role="heading"
        aria-level={headingsAriaLevel}
      >
        {texts.pages.fundList.documents.title}
      </div>

      <StyledDivider margin="16" />

      {monthlyReport && (
        <DownloadLink
          url={`${fundDataUrl}${monthlyReport.url}`}
          title={`${
            texts.pages.fundList.documents.monthlyReport
          } (${getLanguageAsText(monthlyReport.languageCode!)})`}
          data-test="monthly-report"
          data-trackid={getTrackId(
            `download-monthly-report-for-${isin}`,
            TrackingElement.Link,
          )}
        />
      )}

      {kiid && (
        <DownloadLink
          url={`${fundDataUrl}${kiid.url}`}
          title={`${texts.pages.fundList.documents.kiid} (${getLanguageAsText(
            kiid.languageCode!,
          )})`}
          data-test="kiid"
          data-trackid={getTrackId(
            `download-kiid-for-${isin}`,
            TrackingElement.Link,
          )}
        />
      )}

      {prospectus && (
        <DownloadLink
          url={`${fundDataUrl}${prospectus.url}`}
          title={`${
            texts.pages.fundList.documents.prospectus
          } (${getLanguageAsText(prospectus.languageCode!)})`}
          data-test="prospectus"
          data-trackid={getTrackId(
            `download-prospectus-for-${isin}`,
            TrackingElement.Link,
          )}
        />
      )}
    </DocumentsContainer>
  );
};

const DocumentsContainer = styled.div`
  /* Make the container take all the space it can. */
  flex: 1;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 0;
`;
